import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRightLeft, Plus } from "lucide-react";
import { useDeleteWaTemplate, useTenantOrganization, useWaTemplateMoveOptions, useWaTemplates } from "../../lib/api";
import AssignTemplate from "./templates/AssignTemplate";
import DeleteTemplate from "./templates/DeleteTemplate";
import MoveTemplates from "./templates/MoveTemplates";
import TemplateComposer from "./templates/TemplateComposer";
import TemplateDetail from "./templates/TemplateDetail";
import TemplateLibrary from "./templates/TemplateLibrary";
import { focusRing } from "./templates/ui";
import { formFromRow } from "./templates/templateFormat";
import { PrimaryButton, SecondaryButton } from "./primitives";

// The templates already made for a brand. Open one to see it, or make a new one.
export default function Templates({ tenantId, brandName, brandSlug }) {
  const { data: rows } = useWaTemplates(tenantId);
  const [view, setView] = useState("list"); // list | detail | new
  const [openId, setOpenId] = useState(null);
  const [seed, setSeed] = useState({ key: 0, form: null, step: 0 });
  const [highlightId, setHighlightId] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const remove = useDeleteWaTemplate(tenantId);
  const [toAssign, setToAssign] = useState(null);
  const { data: org } = useTenantOrganization(tenantId);
  const [moving, setMoving] = useState(false);
  const { data: moveOptions } = useWaTemplateMoveOptions(tenantId);
  const notHere = (moveOptions?.templates || []).filter((t) => !t.here).length;

  // Looked up from the live list, so a status that changes while it is open shows at once.
  const open = (rows || []).find((r) => r.id === openId);

  const created = (row) => {
    setHighlightId(row?.id ?? null);
    setView("list");
    toast.success("Sent to Chat360. You can follow it here until Meta decides.");
  };
  const confirmDelete = async () => {
    try {
      await remove.mutateAsync(toDelete.id);
      toast.success(`${toDelete.name} deleted.`);
      setToDelete(null);
      setView("list");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete the template. Try again in a moment.");
    }
  };
  // `step` is where the form opens: the first, or the last when it is only waiting to be sent.
  const reuse = (row, { step = 0, duplicate = false } = {}) => {
    setSeed((s) => ({ key: s.key + 1, form: formFromRow(row, duplicate), step }));
    setView("new");
    toast.success(duplicate ? "Copied into the form. Give it a new name, then send it." : "Loaded. Fix anything, then press Send for approval.");
  };

  return (
    <div className="mt-6">
      <div hidden={view !== "list"}>
        <TemplateLibrary
          tenantId={tenantId}
          brandName={brandName}
          highlightId={highlightId}
          onOpen={(row) => {
            setOpenId(row.id);
            setView("detail");
          }}
          onResend={(row) => reuse(row, { step: 3 })}
          onDuplicate={(row) => reuse(row, { duplicate: true })}
          onDelete={setToDelete}
          onCreate={() => setView("new")}
          action={
            <>
              {notHere > 0 && (
                <SecondaryButton onClick={() => setMoving(true)}>
                  <ArrowRightLeft size={16} aria-hidden="true" /> Move to new number ({notHere})
                </SecondaryButton>
              )}
              <PrimaryButton onClick={() => setView("new")}>
                <Plus size={16} aria-hidden="true" /> New template
              </PrimaryButton>
            </>
          }
        />
      </div>

      {view === "detail" && open && (
        <TemplateDetail row={open} brandName={brandName} onBack={() => setView("list")} onDelete={setToDelete} onAssign={setToAssign} />
      )}

      {/* The form stays mounted while hidden, so going back to the list does not lose what was typed. */}
      <div hidden={view !== "new"}>
        <div className="mb-6 flex items-center gap-4">
          <button
            type="button"
            onClick={() => setView("list")}
            className={`inline-flex h-10 cursor-pointer items-center gap-2 rounded-full bg-[#F3F4F6] pl-3 pr-4 text-[14px] font-medium text-[#374151] transition-colors duration-150 hover:bg-[#E5E7EB] ${focusRing}`}
          >
            <ArrowLeft size={16} aria-hidden="true" /> All templates
          </button>
          <h2 className="m-0 text-[20px] font-semibold text-[#111827]">New template</h2>
        </div>
        <TemplateComposer key={seed.key} tenantId={tenantId} brandName={brandName} brandSlug={brandSlug} initial={seed.form} initialStep={seed.step} onCreated={created} />
      </div>
      {toAssign && (
        <AssignTemplate
          row={toAssign}
          tenantId={tenantId}
          dealerName={toAssign.dealer_id == null ? "" : (org?.dealers || []).find((d) => d.id === toAssign.dealer_id)?.name || "this dealer"}
          onClose={() => setToAssign(null)}
          onDone={(step) => {
            toast.success(`Saved. ${toAssign.name} is used for ${step}.`);
            setToAssign(null);
          }}
        />
      )}
      {moving && (
        <MoveTemplates
          tenantId={tenantId}
          onClose={() => setMoving(false)}
        />
      )}
      {toDelete && <DeleteTemplate row={toDelete} busy={remove.isPending} onCancel={() => setToDelete(null)} onConfirm={confirmDelete} />}
    </div>
  );
}
